import React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { XCircle } from 'lucide-react';
import { theme } from '../theme';

const BookingFailurePage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Leer parámetros de MercadoPago - puede venir hold_id o booking_id
  const holdId = searchParams.get('hold_id');
  const bookingId = searchParams.get('booking_id') || searchParams.get('external_reference');
  const mpStatusDetail = searchParams.get('status_detail');

  // Styles
  const containerStyle: React.CSSProperties = {
    minHeight: '100vh',
    backgroundColor: theme.colors.background,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '32px',
  };

  const cardStyle: React.CSSProperties = {
    backgroundColor: theme.colors.card,
    borderRadius: '24px',
    padding: '48px',
    maxWidth: '500px',
    width: '100%',
    textAlign: 'center',
    border: `2px solid ${theme.colors.danger}`,
    boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)',
  };

  const iconContainerStyle: React.CSSProperties = {
    width: '80px',
    height: '80px',
    margin: '0 auto 24px',
    backgroundColor: 'rgba(239, 68, 68, 0.1)',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  };

  const titleStyle: React.CSSProperties = {
    fontSize: '2rem',
    fontWeight: theme.fonts.weightBold,
    color: theme.colors.text,
    marginBottom: '16px',
  };

  const messageStyle: React.CSSProperties = {
    fontSize: theme.fonts.sizeBody,
    color: theme.colors.textSecondary,
    marginBottom: '32px',
    lineHeight: '1.6',
  };

  const buttonStyle: React.CSSProperties = {
    padding: '16px 32px',
    backgroundColor: theme.colors.primary,
    color: '#181A1B',
    border: 'none',
    borderRadius: '12px',
    fontSize: theme.fonts.sizeBody,
    fontWeight: theme.fonts.weightBold,
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    marginRight: '12px',
  };

  const secondaryButtonStyle: React.CSSProperties = {
    ...buttonStyle,
    backgroundColor: theme.colors.border,
    color: theme.colors.text,
  };

  // Obtener mensaje de error basado en status_detail de MercadoPago
  const getErrorMessage = () => {
    switch (mpStatusDetail) {
      case 'cc_rejected_bad_filled_card_number':
        return 'El número de tarjeta es incorrecto.';
      case 'cc_rejected_bad_filled_date':
        return 'La fecha de vencimiento es incorrecta.';
      case 'cc_rejected_bad_filled_other':
        return 'Algunos datos de la tarjeta son incorrectos.';
      case 'cc_rejected_bad_filled_security_code':
        return 'El código de seguridad es incorrecto.';
      case 'cc_rejected_blacklist':
        return 'No pudimos procesar tu pago por un problema con esta tarjeta.';
      case 'cc_rejected_call_for_authorize':
        return 'Debés autorizar el pago llamando a tu banco.';
      case 'cc_rejected_card_disabled':
        return 'La tarjeta no está habilitada para pagos online.';
      case 'cc_rejected_duplicated_payment':
        return 'Ya existe un pago con estos datos. Verificá tus movimientos.';
      case 'cc_rejected_high_risk':
        return 'El pago fue rechazado por seguridad.';
      case 'cc_rejected_insufficient_amount':
        return 'Fondos insuficientes en la tarjeta.';
      case 'cc_rejected_invalid_installments':
        return 'No se puede pagar en esas cuotas con esta tarjeta.';
      case 'cc_rejected_max_attempts':
        return 'Llegaste al límite de intentos permitidos.';
      case 'cc_rejected_other_reason':
      default:
        return 'Tu pago no pudo ser procesado. Por favor verificá tus datos e intentá nuevamente.';
    }
  };

  const handleRetry = () => {
    if (holdId) {
      // Si tenemos hold_id, el hold ya expiró, volver a venue detail para reintentar
      // Extraer venue ID del search params si está disponible
      const venueId = searchParams.get('venue_id');
      if (venueId) {
        navigate(`/venues/${venueId}`);
      } else {
        navigate('/venues');
      }
    } else if (bookingId) {
      // Legacy: redirigir a la página de pago para reintentar
      navigate(`/payment/${bookingId}`);
    } else {
      navigate('/venues');
    }
  };

  return (
    <div style={containerStyle}>
      <div style={cardStyle}>
        <div style={iconContainerStyle}>
          <XCircle size={40} color={theme.colors.danger} />
        </div>
        <h1 style={titleStyle}>Pago Rechazado</h1>
        <p style={messageStyle}>
          {getErrorMessage()}
        </p>
        <div style={{ display: 'flex', justifyContent: 'center', gap: '12px' }}>
          <button
            style={buttonStyle}
            onClick={handleRetry}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 6px 25px rgba(0, 230, 118, 0.3)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            Intentar Nuevamente
          </button>
          <button
            style={secondaryButtonStyle}
            onClick={() => navigate('/venues')}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            Volver a Búsqueda
          </button>
        </div>
      </div>
    </div>
  );
};

export default BookingFailurePage;
